import React, { useState, useEffect, useContext } from 'react';
import { XMarkIcon, TrashIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { GlobalContext } from '../../context/GlobalContext';
import messengerApi from '../../lib/messengerApi';
import usersApi from '../../lib/usersApi';
import { successToast, errorToast } from '../../lib/helpers';

/**
 * Modal for channel settings (edit, delete, manage members)
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Handler for closing modal
 * @param {Object} channel - Channel object
 * @param {number} userId - Current user's ID
 * @param {Function} onChannelUpdated - Callback when channel is updated
 * @param {Function} onChannelDeleted - Callback when channel is deleted
 * @param {Function} onMembersUpdated - Callback when members are added/removed
 */
const ChannelSettingsModal = ({ isOpen, onClose, channel, userId, onChannelUpdated, onChannelDeleted, onMembersUpdated }) => {
  const { uinfo } = useContext(GlobalContext);
  const [activeTab, setActiveTab] = useState('edit'); // 'edit', 'members'
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    branch: ''
  });
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);

  useEffect(() => {
    if (isOpen && channel) {
      setFormData({
        name: channel.name || '',
        description: channel.description || '',
        branch: channel.branch || ''
      });
      fetchMembers();
      fetchAllUsers();
    }
  }, [isOpen, channel]);

  const fetchMembers = async () => {
    if (!channel) return;
    try {
      setLoadingMembers(true);
      const response = await messengerApi.get(`/api/messenger/channels/${channel.id}/members`, {
        params: { user_id: userId }
      });
      if (response.success) {
        setMembers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await usersApi.get('/api/users');
      if (response.success) {
        setAllUsers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleUpdateChannel = async (e) => {
    e.preventDefault();
    if (!channel) return;

    try {
      setIsSubmitting(true);
      const response = await messengerApi.put(`/api/messenger/channels/${channel.id}?user_id=${userId}`, {
        ...formData
      });
      
      if (response.success) {
        successToast('Channel updated successfully');
        onChannelUpdated(response.data);
        onClose();
      }
    } catch (error) {
      console.error('Error updating channel:', error);
      errorToast(error.message || 'Failed to update channel');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteChannel = async () => {
    if (!channel || !window.confirm(`Are you sure you want to delete "${channel.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsDeleting(true);
      const response = await messengerApi.delete(`/api/messenger/channels/${channel.id}?user_id=${userId}`);
      
      if (response.success) {
        successToast('Channel deleted successfully');
        onChannelDeleted(channel.id);
        onClose();
      }
    } catch (error) {
      console.error('Error deleting channel:', error);
      errorToast(error.message || 'Failed to delete channel');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId || !channel) return;

    try {
      setIsSubmitting(true);
      const response = await messengerApi.post(`/api/messenger/channels/${channel.id}/members?user_id=${userId}`, {
        user_id: parseInt(selectedUserId),
        display_name: displayName.trim() || null
      });

      if (response.success) {
        successToast('Member added successfully');
        setSelectedUserId('');
        setDisplayName('');
        fetchMembers(); // Update modal's local state
        if (onMembersUpdated) {
          onMembersUpdated(); // Refresh parent's members list
        }
      }
    } catch (error) {
      console.error('Error adding member:', error);
      errorToast(error.message || 'Failed to add member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!channel || !window.confirm('Remove this member from the channel?')) return;

    try {
      const response = await messengerApi.delete(`/api/messenger/channels/${channel.id}/members/${memberId}?user_id=${userId}`);

      if (response.success) {
        successToast('Member removed successfully');
        fetchMembers(); // Update modal's local state
        if (onMembersUpdated) {
          onMembersUpdated(); // Refresh parent's members list
        }
      }
    } catch (error) {
      console.error('Error removing member:', error);
      errorToast(error.message || 'Failed to remove member');
    }
  };

  if (!isOpen || !channel) return null;

  // Check if user is admin - ensure type matching (both as numbers)
  // Only check admin status if members have been loaded (not loading)
  const currentUserMember = loadingMembers ? null : members.find(m => {
    const memberId = typeof m.user_id === 'string' ? parseInt(m.user_id) : m.user_id;
    const currentUserId = typeof userId === 'string' ? parseInt(userId) : userId;
    return memberId === currentUserId;
  });
  
  // Check if user is channel admin OR system admin
  const isChannelAdmin = currentUserMember?.role === 'admin';
  const isSystemAdmin = uinfo?.Status === 'admin';
  const isAdmin = isChannelAdmin || isSystemAdmin;
  
  // Debug logging
  if (userId) {
    console.log('[ChannelSettings] userId:', userId, 'type:', typeof userId);
    console.log('[ChannelSettings] uinfo.Status:', uinfo?.Status);
    console.log('[ChannelSettings] members:', members);
    console.log('[ChannelSettings] currentUserMember:', currentUserMember);
    console.log('[ChannelSettings] isChannelAdmin:', isChannelAdmin, 'isSystemAdmin:', isSystemAdmin, 'isAdmin:', isAdmin);
  }
  
  const availableUsers = allUsers.filter(user => 
    !members.some(m => parseInt(m.user_id) === parseInt(user.user_id) || m.user_id === user.user_id)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Channel Settings</h2>
          <button
            onClick={onClose}
            disabled={isSubmitting || isDeleting}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('edit')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'edit'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Edit Channel
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'members'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Members ({members.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'edit' ? (
            <form onSubmit={handleUpdateChannel} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  disabled={!isAdmin || isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  disabled={!isAdmin || isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Branch
                </label>
                <input
                  type="text"
                  value={formData.branch}
                  onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                  placeholder="Leave empty for company-wide"
                  disabled={!isAdmin || isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                />
              </div>

              {!isAdmin && (
                <p className="text-sm text-gray-500">Only channel admins can edit channel settings.</p>
              )}

              <div className="flex items-center justify-between pt-4">
                <button
                  type="button"
                  onClick={handleDeleteChannel}
                  disabled={!isAdmin || isDeleting}
                  className="px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  <TrashIcon className="w-4 h-4" />
                  <span>Delete Channel</span>
                </button>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting || isDeleting}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!isAdmin || isSubmitting || isDeleting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {/* Add Member */}
              {isAdmin && (
                <div className="border-b pb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add Member
                  </label>
                  <div className="space-y-2">
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select a user...</option>
                      {availableUsers.map(user => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.username} ({user.email})
                        </option>
                      ))}
                    </select>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Display Name (optional)
                      </label>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="e.g., David"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leave empty to use username or email
                      </p>
                    </div>
                    <button
                      onClick={handleAddMember}
                      disabled={!selectedUserId || isSubmitting}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                    >
                      <UserPlusIcon className="w-4 h-4" />
                      <span>Add Member</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Members List */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel Members
                </label>
                {loadingMembers ? (
                  <div className="text-center py-4 text-gray-500">Loading members...</div>
                ) : members.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">No members</div>
                ) : (
                  <div className="space-y-2">
                    {members.map((member) => {
                      const name = member.display_name || member.username || member.email || 'Unknown';
                      const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
                      const actualName = member.username || member.email || 'Unknown';
                      return (
                        <div key={member.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                              {initials}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{name}</div>
                              {member.display_name && (
                                <div className="text-xs text-gray-500">{actualName}</div>
                              )}
                              <div className="text-xs text-gray-500">
                                {member.role === 'admin' ? 'Admin' : 'Member'}
                              </div>
                            </div>
                          </div>
                          {isAdmin && member.user_id !== userId && (
                            <button
                              onClick={() => handleRemoveMember(member.user_id)}
                              className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChannelSettingsModal;

